'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Box, Tooltip } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { DataGridColumn, RoleChip, Badge } from '@repo/components';
import GenericDataTab from 'src/components/GenericDataTab';
import { Role, UserAccess } from '@repo/shared/domain';
import { stringToSort } from '@repo/shared/helpers';
import { getUserAccess, getExternalAccess } from 'src/api/admin/access';
import { getUser } from 'src/api/admin/users';
import { getFeatureFlags } from 'src/api/settings';
const accessTypes = {
	permanent: 'Permanent',
	event: 'Event-Based',
	schedule: 'Schedule-Based',
	single: 'Single Entry',
	temporary: 'Activation-Based',
} as const;

export const Route = createFileRoute('/_admin/users/$userId/access')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: (search.page as number) || 0,
			pageSize: (search.pageSize as string) || '10',
			sort: (search.sort as string) || 'name:asc',
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const { userId } = params;
		const page = deps.page || 0;
		const pageSize = parseInt(deps.pageSize || '10');

		const [user, featureFlags] = await Promise.all([getUser(userId), getFeatureFlags()]);

		// Use external endpoint if feature flag is enabled and user is readOnly
		const useExternalEndpoint = featureFlags.USES_EXTERNAL_ZONE_ACCESS && user?.readOnly;
		const access = useExternalEndpoint
			? await getExternalAccess(userId, { page, pageSize })
			: await getUserAccess(userId, { page, pageSize });

		return { access, user, useExternalEndpoint };
	},
	component: AccessTab,
});

function AccessTab() {
	const { userId } = Route.useParams();
	const { page, pageSize, sort } = Route.useSearch();
	const { access: initialAccess, useExternalEndpoint } = Route.useLoaderData();

	const pageNum = parseInt(page || '0');
	const pageSizeNum = parseInt(pageSize || '10');

	const { data: access, refetch: refetchAccess } = useQuery({
		queryKey: ['user-access', userId, pageNum, pageSizeNum, useExternalEndpoint],
		queryFn: () =>
			useExternalEndpoint
				? getExternalAccess(userId, { page: pageNum, pageSize: pageSizeNum })
				: getUserAccess(userId, { page: pageNum, pageSize: pageSizeNum }),
		initialData: initialAccess,
	});

	const columns: DataGridColumn<UserAccess>[] = [
		{
			field: 'hasAccess',
			headerName: '',
			width: 50,
			renderCell: ({ value }) => (
				<Box
					sx={{
						width: 28,
						height: 28,
						borderRadius: '50%',
						backgroundColor: value ? 'success.main' : 'error.main',
					}}
				/>
			),
		},
		{
			field: 'zone.name',
			headerName: 'Zone',
			flex: 1,
			sortable: false,
		},
		{
			field: 'roles',
			headerName: 'Roles',
			flex: 1,
			sortable: false,
			renderCell: (c: { value: Role[] }) => (
				<div className="flex flex-wrap gap-1">
					{c.value.map((r: Role) => (
						<RoleChip key={r.name} role={r} />
					))}
				</div>
			),
		},
		{
			field: 'accessType',
			headerName: 'Access Type',
			flex: 1,
			sortable: false,
			renderCell: ({ row }) => {
				const schedule = row.schedule;
				if (row.accessType === 'schedule' && schedule && schedule.times?.length > 0) {
					return (
						<Tooltip
							title={
								<div style={{ fontSize: '1.1em', padding: '4px 0' }}>
									<strong
										style={{
											fontSize: '1.2em',
											display: 'block',
											marginBottom: '8px',
										}}
									>
										Schedule {schedule.day}
									</strong>
									{schedule.times.map((time: { startTime: string; endTime: string }, index: number) => (
										<div key={index} style={{ marginBottom: '4px' }}>
											{time.startTime} - {time.endTime}
										</div>
									))}
								</div>
							}
							arrow
							placement="top"
						>
							<span>{accessTypes[row.accessType as keyof typeof accessTypes]}</span>
						</Tooltip>
					);
				} else if (row.accessType === 'temporary' && row.tempAccessExpiry) {
					return (
						<Tooltip
							title={`Access expires on ${new Date(row.tempAccessExpiry).toLocaleString()}`}
							arrow
							placement="top"
						>
							<span>{accessTypes[row.accessType as keyof typeof accessTypes]}</span>
						</Tooltip>
					);
				}
				return accessTypes[row.accessType as keyof typeof accessTypes];
			},
		},
		{
			field: 'eventsAvailable',
			headerName: 'Events',
			flex: 1,
			sortable: false,
			renderCell: (c) => (
				<div className="flex flex-wrap gap-1">
					{c.value?.map((r: { id: string; name: string }) => (
						<Link key={r.name} to="/events/$eventId" params={{ eventId: r.id }}>
							<Badge>{r.name}</Badge>
						</Link>
					))}
				</div>
			),
		},
	];

	const handleReload = async () => {
		await refetchAccess();
	};

	return (
		<GenericDataTab
			data={access.data}
			columns={columns}
			onReload={handleReload}
			page={access.page}
			pageSize={access.pageSize}
			count={access.count}
			getRowId={({ id }) => id}
			sortModel={[stringToSort(sort || 'name:asc')]}
		/>
	);
}
